import {
  deriveCustomFieldType,
  type CustomFieldType,
} from "@/lib/customFieldTypes";
import type {
  CustomFieldProperty,
  ERPSchemaDefinition,
} from "@forgepoint/shared";
import type { JSONSchema7 } from "json-schema";
import type { FieldOption } from "./CustomFieldRow";
import type { ErpPropertyOption } from "./SelectOptionMappingRows";

/** Extends JSONSchema7 with the non-standard `propertyOrder` extension and ERP select field extensions. */
export interface MappingSchema extends JSONSchema7 {
  propertyOrder?: string[];
  properties?: Record<string, ERPSchemaDefinition>;
}

/** A select field mapping value with per-option value mappings. */
export interface SelectFieldMappingValue {
  erpProperty: string;
  optionMapping: Record<string, string>;
}

/** A mapping value — either a plain HS property name or a select field mapping with per-option values. */
export type CustomFieldMappingValue = string | SelectFieldMappingValue;

/** Describes a single custom field entry with its options and mapping metadata. */
export interface FieldEntry {
  key: string;
  label: string;
  fieldType: CustomFieldType;
  options: FieldOption[];
  /** Whether this field is a select type requiring option-level mapping */
  isSelectField: boolean;
  /** FP select option values (empty for non-select fields) */
  fpFieldOptions: string[];
  /** ERP property options keyed by ERP property name (empty for non-select fields) */
  erpPropertyOptions: Record<string, ErpPropertyOption[]>;
  /** Provider-supplied hint messages per ERP field, keyed by field API name */
  fieldHints: Record<string, string>;
  /** Provider-supplied preferred target used by the user-triggered auto-map helper. */
  defaultTarget?: string;
  /** Whether the provider declared this mapping canonical: auto-filled and not user-editable. */
  isLocked: boolean;
}

interface AutoMapOptionMappingArgs {
  fpOptions: string[];
  erpOptions: ErpPropertyOption[];
  optionMapping: Record<string, string>;
}

interface AutoMapCustomFieldMappingArgs {
  fieldEntries: FieldEntry[];
  mapping: Record<string, CustomFieldMappingValue>;
}

/**
 * Extracts the ERP property name from a mapping value, which may be a
 * plain string or an object with `erpProperty`.
 * @param value - The mapping value, or undefined if not mapped
 * @returns The ERP property name, or empty string if not mapped
 */
export function getMappedErpProperty(
  value: CustomFieldMappingValue | undefined,
): string {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  return value.erpProperty;
}

export function autoMapSelectOptionValues({
  fpOptions,
  erpOptions,
  optionMapping,
}: AutoMapOptionMappingArgs): Record<string, string> {
  const nextMapping = { ...optionMapping };
  const usedErpValues = new Set(
    Object.values(nextMapping).filter((value) => value.length > 0),
  );
  const erpOptionsByName = buildUniqueOptionLookup(erpOptions);

  for (const fpOption of fpOptions) {
    if (nextMapping[fpOption]) {
      continue;
    }

    const matchingOption = getUniqueOptionByName(erpOptionsByName, [fpOption]);
    if (!matchingOption || usedErpValues.has(matchingOption.value)) {
      continue;
    }

    nextMapping[fpOption] = matchingOption.value;
    usedErpValues.add(matchingOption.value);
  }

  return nextMapping;
}

export function autoMapCustomFieldMapping({
  fieldEntries,
  mapping,
}: AutoMapCustomFieldMappingArgs): Record<string, CustomFieldMappingValue> {
  const nextMapping = cloneCustomFieldMapping(mapping);
  const usedErpProperties = collectMappedErpProperties(nextMapping);
  let hasChanges = false;

  for (const entry of fieldEntries) {
    const currentValue = nextMapping[entry.key];
    let erpProperty = getMappedErpProperty(currentValue);

    if (!erpProperty) {
      const matchingOption = findMatchingFieldOption(entry, usedErpProperties);
      if (!matchingOption) {
        continue;
      }

      erpProperty = matchingOption.value;
      usedErpProperties.add(erpProperty);
      nextMapping[entry.key] = entry.isSelectField
        ? { erpProperty, optionMapping: {} }
        : erpProperty;
      hasChanges = true;
    }

    if (!entry.isSelectField) {
      continue;
    }

    const erpOptions = entry.erpPropertyOptions[erpProperty] ?? [];
    if (erpOptions.length === 0) {
      continue;
    }

    const nextMappingValue = nextMapping[entry.key];
    const currentOptionMapping =
      typeof nextMappingValue === "object" && nextMappingValue !== null
        ? nextMappingValue.optionMapping
        : {};
    const autoMappedOptionMapping = autoMapSelectOptionValues({
      fpOptions: entry.fpFieldOptions,
      erpOptions,
      optionMapping: currentOptionMapping,
    });

    if (areStringRecordsEqual(currentOptionMapping, autoMappedOptionMapping)) {
      continue;
    }

    nextMapping[entry.key] = {
      erpProperty,
      optionMapping: autoMappedOptionMapping,
    };
    hasChanges = true;
  }

  return hasChanges ? nextMapping : mapping;
}

export function areStringRecordsEqual(
  left: Record<string, string>,
  right: Record<string, string>,
): boolean {
  const leftEntries = Object.entries(left);
  const rightKeys = Object.keys(right);

  return (
    leftEntries.length === rightKeys.length &&
    leftEntries.every(([key, value]) => right[key] === value)
  );
}

export function areCustomFieldMappingsEqual(
  left: Record<string, CustomFieldMappingValue>,
  right: Record<string, CustomFieldMappingValue>,
): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every((key) =>
      areCustomFieldMappingValuesEqual(left[key], right[key]),
    )
  );
}

/**
 * Extracts the list of custom field entries from the mapping schema. Each
 * entry's options are parsed from `enum`/`enumNames`, with the group name
 * extracted from the `[groupName] Label` format used by enumNames. Field types
 * are derived from the org's custom fields schema when available. Select fields
 * include additional metadata for option-level mapping.
 * @param mappingSchema - The opportunityCustomFieldMapping sub-schema
 * @param fieldProperties - The org's custom field property definitions, keyed by field name
 * @returns Ordered field entries with parsed options
 */
export function getFieldEntries(
  mappingSchema: MappingSchema | null,
  fieldProperties: Record<string, CustomFieldProperty> | null,
): FieldEntry[] {
  if (!mappingSchema || !mappingSchema.properties) {
    return [];
  }

  const { properties } = mappingSchema;

  const propertyOrder = Array.isArray(mappingSchema.propertyOrder)
    ? mappingSchema.propertyOrder
    : Object.keys(properties);

  return propertyOrder
    .filter((key) => properties[key] !== undefined)
    .map((key) => {
      const propDef = properties[key];
      if (typeof propDef === "boolean") {
        return {
          key,
          label: key,
          fieldType: "string",
          options: [],
          isSelectField: false,
          fpFieldOptions: [],
          erpPropertyOptions: {},
          fieldHints: {},
          defaultTarget: undefined,
          isLocked: false,
        };
      }
      const label = propDef.title ?? key;
      const fieldType = deriveCustomFieldType(fieldProperties?.[key] ?? null);

      const isSelectField = propDef.isSelectField === true;

      return {
        key,
        label,
        fieldType,
        options: parseFieldOptions(propDef),
        isSelectField,
        fpFieldOptions: isSelectField ? (propDef.fpFieldOptions ?? []) : [],
        erpPropertyOptions: isSelectField
          ? (propDef.erpPropertyOptions ?? {})
          : {},
        fieldHints: propDef.fieldHints ?? {},
        defaultTarget:
          typeof propDef.default === "string" ? propDef.default : undefined,
        isLocked:
          propDef.readOnly === true && typeof propDef.default === "string",
      };
    });
}

/**
 * Parses a schema property's `enum`/`enumNames` into structured options.
 * The `enumNames` format is `[groupName] Label`; if the bracket prefix is
 * missing the full string is used as the label with an empty group.
 * @param propDef - The JSON schema definition for the field
 * @returns Parsed options with value, label, and groupName
 */
function parseFieldOptions(propDef: ERPSchemaDefinition): FieldOption[] {
  if (typeof propDef === "boolean" || !Array.isArray(propDef.enum)) {
    return [];
  }

  const enumNames: string[] | undefined = Array.isArray(propDef.enumNames)
    ? propDef.enumNames
    : undefined;

  return propDef.enum
    .filter((v): v is string => typeof v === "string")
    .map((value, index) => {
      const rawLabel = enumNames?.[index] ?? value;
      const match = rawLabel.match(/^\[(.+?)\]\s*(.+)$/);
      return {
        value,
        label: match ? match[2] : rawLabel,
        groupName: match ? match[1] : "",
      };
    });
}

function cloneCustomFieldMapping(
  mapping: Record<string, CustomFieldMappingValue>,
): Record<string, CustomFieldMappingValue> {
  const result: Record<string, CustomFieldMappingValue> = {};
  for (const [key, value] of Object.entries(mapping)) {
    result[key] =
      typeof value === "string"
        ? value
        : {
            erpProperty: value.erpProperty,
            optionMapping: { ...value.optionMapping },
          };
  }
  return result;
}

function collectMappedErpProperties(
  mapping: Record<string, CustomFieldMappingValue>,
): Set<string> {
  const used = new Set<string>();
  for (const value of Object.values(mapping)) {
    const erpProperty = getMappedErpProperty(value);
    if (erpProperty) {
      used.add(erpProperty);
    }
  }
  return used;
}

function findMatchingFieldOption(
  entry: FieldEntry,
  usedErpProperties: ReadonlySet<string>,
): FieldOption | null {
  const availableOptions = entry.options.filter(
    (option) => !usedErpProperties.has(option.value),
  );
  const defaultOption = entry.defaultTarget
    ? availableOptions.find((option) => option.value === entry.defaultTarget)
    : undefined;
  if (defaultOption) {
    return defaultOption;
  }
  const optionsByName = buildUniqueOptionLookup(availableOptions);

  return getUniqueOptionByName(optionsByName, [entry.label, entry.key]);
}

function buildUniqueOptionLookup<
  TOption extends { label: string; value: string },
>(options: TOption[]): Map<string, TOption | null> {
  const optionsByName = new Map<string, TOption | null>();

  for (const option of options) {
    const names = new Set([
      normalizeMappingName(option.label),
      normalizeMappingName(option.value),
    ]);

    for (const name of names) {
      if (!name) {
        continue;
      }

      const existingOption = optionsByName.get(name);
      if (existingOption === undefined) {
        optionsByName.set(name, option);
      } else if (existingOption?.value !== option.value) {
        optionsByName.set(name, null);
      }
    }
  }

  return optionsByName;
}

function getUniqueOptionByName<TOption>(
  optionsByName: Map<string, TOption | null>,
  names: string[],
): TOption | null {
  for (const name of names) {
    const normalizedName = normalizeMappingName(name);
    if (!normalizedName) {
      continue;
    }

    const option = optionsByName.get(normalizedName);
    if (option) {
      return option;
    }
  }

  return null;
}

function normalizeMappingName(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/['\u2019]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase();
}

function areCustomFieldMappingValuesEqual(
  left: CustomFieldMappingValue | undefined,
  right: CustomFieldMappingValue | undefined,
): boolean {
  if (left === undefined || right === undefined) {
    return left === right;
  }
  if (typeof left === "string" || typeof right === "string") {
    return left === right;
  }
  return (
    left.erpProperty === right.erpProperty &&
    areStringRecordsEqual(left.optionMapping, right.optionMapping)
  );
}
